/**
 * ComprehensiveTrendChart Component
 * Used in: Dashboard screen - displays Complete Financial Trends (Monthly)
 */
import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FinancialData, getDataByViewMode } from '../data/financialData';
import { Eye, EyeOff } from 'lucide-react';
import { MobileChartFallback } from './MobileChartFallback';
import { useScreenSize } from '../hooks/useScreenSize';

interface DataSeries {
  key: string;
  name: string;
  color: string;
  strokeWidth: number;
  isPercentage?: boolean;
}

const dataSeries: DataSeries[] = [
  { key: 'totalIncome', name: 'Total Income', color: '#3b82f6', strokeWidth: 2 },
  { key: 'totalCOGS', name: 'Total COGS', color: '#ef4444', strokeWidth: 2 },
  { key: 'grossProfit', name: 'Gross Profit', color: '#10b981', strokeWidth: 2 },
  { key: 'totalExpense', name: 'Total Expense', color: '#f59e0b', strokeWidth: 2 },
  { key: 'netOrdinaryIncome', name: 'Net Income', color: '#8b5cf6', strokeWidth: 3 },
  { key: 'cogsPercentage', name: 'COGS % of Revenue', color: '#dc2626', strokeWidth: 2, isPercentage: true },
];

interface ComprehensiveTrendChartProps {
  viewMode?: 'monthly' | 'yearly';
  data: FinancialData[];
}

export const ComprehensiveTrendChart: React.FC<ComprehensiveTrendChartProps> = ({ viewMode = 'monthly', data }) => {
  // Initialize with only Total Income and Net Income visible by default
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
    new Set(['totalIncome', 'netOrdinaryIncome'])
  );
  
  // Screen size detection
  const { shouldShowFallback, isMobile } = useScreenSize();

  // Get data based on view mode
  const chartData = useMemo(() => getDataByViewMode(viewMode, data), [viewMode, data]);

  // Calculate extended data with COGS percentage
  const extendedData = useMemo(() => {
    return chartData.map(item => ({
      ...item,
      cogsPercentage: item.totalIncome > 0 ? (item.totalCOGS / item.totalIncome) * 100 : 0
    }));
  }, [chartData]);

  // Calculate mean values for each series
  const meanValues = useMemo(() => {
    const means: { [key: string]: number } = {};
    
    dataSeries.forEach(series => {
      if (series.key === 'cogsPercentage') {
        // Calculate mean for COGS percentage
        const validPercentages = extendedData
          .map(item => item.cogsPercentage)
          .filter(val => val !== undefined && !isNaN(val));
        means[series.key] = validPercentages.length > 0 
          ? validPercentages.reduce((sum, val) => sum + val, 0) / validPercentages.length 
          : 0;
      } else {
        // Calculate mean for financial data
        const validValues = chartData
          .map(item => item[series.key as keyof typeof item])
          .filter(val => typeof val === 'number' && !isNaN(val)) as number[];
        means[series.key] = validValues.length > 0 
          ? validValues.reduce((sum, val) => sum + val, 0) / validValues.length 
          : 0;
      }
    });
    
    return means;
  }, [extendedData]);

  const toggleSeries = (seriesKey: string) => {
    const newVisible = new Set(visibleSeries);
    if (newVisible.has(seriesKey)) {
      newVisible.delete(seriesKey);
    } else {
      newVisible.add(seriesKey);
    }
    setVisibleSeries(newVisible);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Check if any percentage series are visible
  const hasPercentageSeries = Array.from(visibleSeries).some(key => 
    dataSeries.find(series => series.key === key)?.isPercentage
  );

  // Get visible currency series means for Y-axis annotation
  const visibleCurrencyMeans = dataSeries
    .filter(series => !series.isPercentage && visibleSeries.has(series.key))
    .map(series => ({
      value: meanValues[series.key],
      color: series.color,
      name: series.name
    }))
    .sort((a, b) => b.value - a.value); // Sort by value descending

  // Determine if we should render a right-side axis with mean ticks (only when no percentage axis is needed)
  const shouldShowRightMeansAxis = visibleCurrencyMeans.length > 0 && !hasPercentageSeries;
  const rightMargin = shouldShowRightMeansAxis || hasPercentageSeries ? 60 : 30;

  // Get visible percentage series means for right Y-axis annotation  
  const visiblePercentageMeans = dataSeries
    .filter(series => series.isPercentage && visibleSeries.has(series.key))
    .map(series => ({
      value: meanValues[series.key],
      color: series.color,
      name: series.name
    }))
    .sort((a, b) => b.value - a.value); // Sort by value descending

  // Custom Y-axis tick formatter that highlights mean values
  const customYAxisTickFormatter = (value: number) => {
    const meanMatch = visibleCurrencyMeans.find(mean => 
      Math.abs(mean.value - value) < Math.abs(mean.value * 0.02) // Within 2% tolerance
    );
    
    if (meanMatch) {
      return `${formatCurrency(value)} (μ)`; // μ symbol for mean
    }
    
    return formatCurrency(value);
  };

  // Custom percentage Y-axis tick formatter that highlights mean values
  const customPercentageTickFormatter = (value: number) => {
    const meanMatch = visiblePercentageMeans.find(mean => 
      Math.abs(mean.value - value) < Math.abs(mean.value * 0.02) // Within 2% tolerance
    );
    
    if (meanMatch) {
      return `${value}% (μ)`; // μ symbol for mean
    }
    
    return `${value}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => {
            const series = dataSeries.find(s => s.key === entry.dataKey);
            const formattedValue = series?.isPercentage 
              ? formatPercentage(entry.value)
              : formatCurrency(entry.value);
            
            return (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {formattedValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Show fallback for very narrow screens
  if (shouldShowFallback) {
    const formatCurrency = (value: number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);

    return (
      <MobileChartFallback
        title={`Complete Financial Trends (${viewMode === 'yearly' ? 'Yearly' : 'Monthly'})`}
        data={extendedData}
        dataKeys={['totalIncome', 'totalCOGS', 'netOrdinaryIncome']}
        formatValue={formatCurrency}
        showRotateMessage={true}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 lg:mb-0">
          Complete Financial Trends ({viewMode === 'yearly' ? 'Yearly' : 'Monthly'})
        </h3>
        <div className="flex flex-wrap gap-2">
          {dataSeries.map((series) => (
            <button
              key={series.key}
              onClick={() => toggleSeries(series.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                visibleSeries.has(series.key)
                  ? 'bg-gray-100 text-gray-900 border border-gray-200'
                  : 'bg-gray-50 text-gray-500 border border-gray-100'
              }`}
            >
              {visibleSeries.has(series.key) ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: visibleSeries.has(series.key) ? series.color : '#d1d5db' }}
              />
              {series.name}
            </button>
          ))}
        </div>
      </div>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer 
          width="100%" 
          height={450} 
          minWidth={300}
        >
          <LineChart 
            data={extendedData} 
            margin={{ 
              top: 20, 
              right: 20, 
              left: 10, 
              bottom: 5 
            }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="month" 
            stroke="#6b7280"
            fontSize={isMobile ? 9 : 10}
            angle={-45}
            textAnchor="end"
            height={isMobile ? 40 : 60}
            interval={isMobile ? 3 : "preserveStartEnd"}
          />
          <YAxis 
            yAxisId="currency"
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={customYAxisTickFormatter}
          />
          {shouldShowRightMeansAxis && (
            <YAxis
              yAxisId="means"
              orientation="right"
              stroke="#6b7280"
              fontSize={10}
              domain={["dataMin", "dataMax"]}
              tickFormatter={(value) => formatCurrency(value as unknown as number)}
              ticks={visibleCurrencyMeans.map(m => m.value)}
            />
          )}
          {hasPercentageSeries && (
            <YAxis 
              yAxisId="percentage"
              orientation="right"
              stroke="#dc2626"
              fontSize={10}
              domain={[0, 'dataMax']}
              tickFormatter={() => ''}
              axisLine={false}
              tickLine={false}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" yAxisId="currency" />
          
          {/* Mean reference lines for each visible series */}
          {dataSeries.map((series) => {
            if (!visibleSeries.has(series.key)) return null;
            
            const meanValue = meanValues[series.key];
            const yAxisId = series.isPercentage ? "percentage" : "currency";
            const formattedMean = series.isPercentage 
              ? formatPercentage(meanValue)
              : formatCurrency(meanValue);
            
            return (
              <ReferenceLine
                key={`mean-${series.key}`}
                y={meanValue}
                stroke={series.color}
                strokeDasharray="5 5"
                strokeWidth={1.5}
                yAxisId={yAxisId}
                label={{
                  value: `μ ${formattedMean}`,
                  position: "right",
                  offset: 5,
                  style: { fontSize: '10px', fill: series.color, fontWeight: '600' }
                }}
              />
            );
          })}
          
          {/* Data lines */}
          {dataSeries.map((series) => (
            visibleSeries.has(series.key) && (
              <Line 
                key={series.key}
                type="monotone" 
                dataKey={series.key} 
                stroke={series.color} 
                strokeWidth={series.strokeWidth}
                name={series.name}
                dot={false}
                activeDot={{ r: 6, stroke: series.color, strokeWidth: 2 }}
                yAxisId={series.isPercentage ? "percentage" : "currency"}
              />
            )
          ))}
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};